import { Option, Schema } from "effect";

/** DNS-label-safe routing identity; the double hyphen separates host domains. */
export const WorkerName = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,62}$/),
  Schema.makeFilter(value => !value.includes("--"), { expected: "a worker name without the host separator" })
).pipe(Schema.brand("worker-name")))();
export type WorkerName = typeof WorkerName.Type;

const PackageName = /* @__PURE__ */ (() => Schema.fromJsonString(Schema.Struct({ name: Schema.String })))();
const packageName = /* @__PURE__ */ (() => Schema.decodeUnknownOption(PackageName))();

interface ConfigToken {
  text: string;
  literal?: string;
}

const REGEX_PREFIX_TOKENS = [
  "(",
  "[",
  "{",
  ",",
  ";",
  ":",
  "=",
  "=>",
  "?",
  "!",
  "return",
  "throw",
  "case",
  "delete",
  "typeof",
  "void",
  "yield",
  "await"
];

function tokenizeConfig(source: string): ConfigToken[] {
  const tokens: ConfigToken[] = [];
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    const next = source[index + 1];
    if (/\s/u.test(character)) continue;
    if (character === "/" && next === "/") {
      const end = source.indexOf("\n", index + 2);
      if (end < 0) break;
      index = end;
      continue;
    }
    if (character === "/" && next === "*") {
      const end = source.indexOf("*/", index + 2);
      if (end < 0) break;
      index = end + 1;
      continue;
    }
    if (
      character === "/" &&
      (tokens.length === 0 ||
        REGEX_PREFIX_TOKENS.includes(tokens[tokens.length - 1].text))
    ) {
      let characterClass = false;
      for (index += 1; index < source.length; index += 1) {
        const value = source[index];
        if (value === "\\") index += 1;
        else if (value === "[") characterClass = true;
        else if (value === "]") characterClass = false;
        else if (value === "/" && !characterClass) {
          while (/[A-Za-z]/u.test(source[index + 1] ?? "")) index += 1;
          break;
        }
      }
      tokens.push({ text: "regex" });
      continue;
    }
    if ('"\'`'.includes(character)) {
      const delimiter = character;
      let literal = "";
      let dynamic = false;
      for (index += 1; index < source.length; index += 1) {
        const value = source[index];
        if (value === "\\" && index + 1 < source.length) {
          literal += source[index + 1];
          index += 1;
        } else if (value === delimiter) {
          break;
        } else {
          if (delimiter === "`" && value === "$" && source[index + 1] === "{") {
            dynamic = true;
          }
          literal += value;
        }
      }
      tokens.push({
        text: "string",
        ...(dynamic ? {} : { literal })
      });
      continue;
    }
    if (/[A-Za-z_$]/u.test(character)) {
      let end = index + 1;
      while (end < source.length && /[\w$]/u.test(source[end])) end += 1;
      tokens.push({ text: source.slice(index, end) });
      index = end - 1;
      continue;
    }
    if (character === "=" && next === ">") {
      tokens.push({ text: "=>" });
      index += 1;
      continue;
    }
    tokens.push({ text: character });
  }
  return tokens;
}

function afterBalanced(
  tokens: ConfigToken[],
  index: number,
  open: string,
  close: string
): number | undefined {
  let depth = 0;
  for (; index < tokens.length; index += 1) {
    if (tokens[index].text === open) depth += 1;
    else if (tokens[index].text === close && --depth === 0) return index + 1;
  }
  return undefined;
}

function literalObjectName(
  tokens: ConfigToken[],
  objectIndex: number
): string | undefined {
  let depth = 0;
  let propertyStart = false;
  for (let index = objectIndex; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token.text === "{") {
      depth += 1;
      if (depth === 1) propertyStart = true;
      continue;
    }
    if (token.text === "}") {
      depth -= 1;
      if (depth === 0) break;
      continue;
    }
    if (depth !== 1) continue;
    if (token.text === ",") {
      propertyStart = true;
      continue;
    }
    if (
      propertyStart &&
      token.text === "name" &&
      tokens[index + 1]?.text === ":" &&
      tokens[index + 2]?.literal !== undefined
    ) {
      return tokens[index + 2].literal;
    }
    propertyStart = false;
  }
  return undefined;
}

function objectFromExpression(
  tokens: ConfigToken[],
  index: number
): number | undefined {
  while (tokens[index]?.text === "(") index += 1;
  if (tokens[index]?.text === "{") return index;
  if (tokens[index]?.text === "defineWorker") {
    return objectFromDefineWorker(tokens, index);
  }
  return undefined;
}

function returnedObjectFromBlock(
  tokens: ConfigToken[],
  blockIndex: number
): number | undefined {
  let depth = 0;
  for (let index = blockIndex; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token.text === "{") {
      depth += 1;
      continue;
    }
    if (token.text === "}") {
      depth -= 1;
      if (depth === 0) break;
      continue;
    }
    if (depth === 1 && token.text === "return") {
      const returned = objectFromExpression(tokens, index + 1);
      if (returned !== undefined) return returned;
    }
  }
  return undefined;
}

function objectFromDefineWorker(
  tokens: ConfigToken[],
  defineIndex: number
): number | undefined {
  let index = defineIndex + 1;
  if (tokens[index]?.text === "<") {
    const afterType = afterBalanced(tokens, index, "<", ">");
    if (afterType === undefined) return undefined;
    index = afterType;
  }
  if (tokens[index]?.text !== "(") return undefined;
  const callIndex = index;
  index += 1;
  if (tokens[index]?.text === "{") return index;

  let parentheses = 0;
  let brackets = 0;
  let braces = 0;
  let angles = 0;
  for (; index < tokens.length; index += 1) {
    const token = tokens[index].text;
    if (
      token === "=>" &&
      parentheses === 0 &&
      brackets === 0 &&
      braces === 0 &&
      angles === 0
    ) {
      let resultIndex = index + 1;
      let wrappers = 0;
      while (tokens[resultIndex]?.text === "(") {
        wrappers += 1;
        resultIndex += 1;
      }
      if (tokens[resultIndex]?.text === "{") {
        return wrappers > 0
          ? resultIndex
          : returnedObjectFromBlock(tokens, resultIndex);
      }
      return objectFromExpression(tokens, resultIndex);
    }
    if (token === "(") parentheses += 1;
    else if (token === ")") {
      if (parentheses === 0 && brackets === 0 && braces === 0) break;
      parentheses -= 1;
    } else if (token === "[") brackets += 1;
    else if (token === "]") brackets -= 1;
    else if (token === "{") braces += 1;
    else if (token === "}") braces -= 1;
    else if (token === "<") angles += 1;
    else if (token === ">" && angles > 0) angles -= 1;
  }

  if (tokens[callIndex + 1]?.text === "(") {
    const afterWrapper = afterBalanced(tokens, callIndex + 1, "(", ")");
    if (afterWrapper !== undefined && tokens[afterWrapper]?.text !== "=>") {
      return objectFromExpression(tokens, callIndex + 1);
    }
  }
  return undefined;
}

function declaredConfigObject(
  tokens: ConfigToken[],
  name: string
): number | undefined {
  for (let index = 0; index < tokens.length - 2; index += 1) {
    if (
      !["const", "let", "var"].includes(tokens[index].text) ||
      tokens[index + 1].text !== name
    ) {
      continue;
    }
    for (let cursor = index + 2; cursor < tokens.length; cursor += 1) {
      if (tokens[cursor].text === ";") break;
      if (tokens[cursor].text !== "=") continue;
      const object = objectFromExpression(tokens, cursor + 1);
      if (object !== undefined) return object;
    }
  }
  return undefined;
}

function exportedConfigObject(tokens: ConfigToken[]): number | undefined {
  for (let index = 0; index < tokens.length - 2; index += 1) {
    if (tokens[index].text !== "export" || tokens[index + 1].text !== "default") {
      continue;
    }
    const expressionIndex = index + 2;
    const direct = objectFromExpression(tokens, expressionIndex);
    if (direct !== undefined) return direct;
    const name = tokens[expressionIndex].text;
    return declaredConfigObject(tokens, name);
  }
  return undefined;
}

function declaredWorkerName(tokens: ConfigToken[]): string | undefined {
  for (let index = 0; index < tokens.length - 3; index += 1) {
    if (tokens[index].text !== "const" || tokens[index + 1].text !== "workerName") {
      continue;
    }
    for (let cursor = index + 2; cursor < tokens.length; cursor += 1) {
      if (tokens[cursor].text === ";") break;
      if (
        tokens[cursor].text === "=" &&
        tokens[cursor + 1]?.literal !== undefined
      ) {
        return tokens[cursor + 1].literal;
      }
    }
  }
  return undefined;
}

function literalConfigWorkerName(config: string): string | undefined {
  const tokens = tokenizeConfig(config);
  let objectIndex = exportedConfigObject(tokens);
  if (objectIndex === undefined) {
    for (let index = 0; index < tokens.length; index += 1) {
      if (tokens[index].text !== "defineWorker") continue;
      objectIndex = objectFromDefineWorker(tokens, index);
      if (objectIndex !== undefined) break;
    }
  }
  return (
    (objectIndex === undefined
      ? undefined
      : literalObjectName(tokens, objectIndex)) ?? declaredWorkerName(tokens)
  );
}

/** Best-effort routing name before config evaluation builds the Worker. */
export function workerNameFromFiles(files: Record<string, string>): string {
  const config =
    files["cloudflare.config.ts"] ?? files["cloudflare.config.js"];
  const fromConfig = config && literalConfigWorkerName(config);
  if (fromConfig) return fromConfig;
  return Option.match(packageName(files["package.json"] ?? "{}"), {
    onSome: metadata => metadata.name,
    onNone: () => "worker"
  });
}
